import React from 'react';
import { Link } from 'react-router-dom';
import PropType from 'prop-types';
import AsyncCreatableSelect from 'react-select/lib/AsyncCreatable';
import axios from 'axios';
import Select from 'react-select';
import settings from '../../settings.json';

const nucleiRegions = settings.regionList.map(region => ({ label: region, value: region }));
const apiHost = process.env.REACT_APP_API_HOST;
const apiProto = process.env.REACT_APP_API_PROTOCOL;

const fetchOptions = (inputValue) => {
  const url = `${apiProto}://${apiHost}/nuclei/gene_list?filter=${inputValue}`;
  return axios.get(url).then((response) => {
    const options = response.data.map(item => ({ value: item.value, label: item.label }));
    return options;
  });
};

class HeatMap extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      geneList: [],
      comparison1: [],
      comparison2: [],
      fdrMax: 0.05,
      lfcMin: 1,
      geneNumber: 10,
      deMode: 'both',
      sortBy: 'P',
      error: null,
    };
  }

  handleSubmit = () => {
    const { match, cellPopulation } = this.props;
    const {
      geneList,
      comparison1,
      comparison2,
      sortBy,
      geneNumber,
      deMode,
      fdrMax,
      lfcMin,
    } = this.state;

    let ord = cellPopulation.get('chosen');

    if (cellPopulation.get('order') === 'alpha') {
      ord = ord.sort();
    } else if (cellPopulation.get('order') === 'grouped') {
      const fullList = settings.regionList;
      ord = fullList.filter(region => ord.has(region));
    }
    // else we leave it as it was selected by the user.

    if (ord.length === 0) {
      this.setState({ error: 'Please select 1 or more cell populations.' });
      return;
    }

    const payload = {
      type: 'heatmap',
      category: 'nuclei',
      de_mode: deMode,
      ord,
    };

    // reset the error message on submit.
    this.setState({ error: null });
    // decide if we are doing a gene list or differential expression heatmap
    // if there is nothing in either the gene field or the comparisons fields then throw an error
    if (geneList.length === 0 && comparison1.length === 0 && comparison2.length === 0) {
      this.setState({ error: 'You must select a set of genes or a comparison.' });
      return;
    }
    // if there is something in both, then throw an error, only want one or the other
    if (geneList.length > 0 && (comparison1.length > 0 || comparison2.length > 0)) {
      this.setState({ error: 'Select either genes or a comparison, not both.' });
      return;
    }
    // if there is something in genes then run the heatmap search
    if (geneList.length > 0 && comparison1.length === 0 && comparison2.length === 0) {
      const genes = this.state.geneList.map(entry => entry.value);
      payload.genes = genes;
    }
    // if there is something in both comparisons run the de_heatmap search
    if (geneList.length === 0 && comparison1.length > 0 && comparison2.length > 0) {
      payload.comparison1 = comparison1.map(entry => entry.value);
      payload.comparison2 = comparison2.map(entry => entry.value);
      payload.sort_by = sortBy;
      payload.topgenes = geneNumber;
      payload.fdr_cutoff = fdrMax;
      payload.lfc_cutoff = lfcMin;
    }

    // TODO: clear out the form for future searches?
    // TODO: validation here??
    if (payload.genes && payload.genes.length > 0) {
      const geneSearchPayload = {
        source: 'nuclei',
        filters: payload.genes,
      };

      const wildcardSearch = `${apiProto}://${apiHost}/gene_search`;

      axios.post(wildcardSearch, geneSearchPayload).then((response) => {
        payload.genes = response.data;
        this.submitSearch(match, payload);
      });
    } else {
      this.submitSearch(match, payload);
    }
  }

  submitSearch(match, payload) {
    // submit values to the backend storage and retrieve the uuid
    axios.post(`${apiProto}://${apiHost}/search`, payload).then((response) => {
      // redirect to the results page which will load in the image and parameters.
      const redirect = `${match.path}/${response.data}`;
      this.props.history.push(redirect);
    });
  }

  render() {
    const { error } = this.state;
    return (
      <div>
        { error && (<div className="alert alert-danger" role="alert">{error}</div>)}
        <div className="form-group">
          <label htmlFor="geneList">Compare individual genes <Link to="/about#geneselection">?</Link></label>
          <AsyncCreatableSelect
            isMulti
            placeholder="Start typing to load gene options"
            noOptionsMessage={() => "No options, please use a less specific filter string. Still can't find your gene of interest? Please see our methods section, by clicking on the '?' next to this search box."}
            formatCreateLabel={input => `search for ${input}`}
            loadOptions={fetchOptions}
            name="geneList"
            value={this.state.geneList}
            onChange={value => this.setState({ geneList: value })}
          />
        </div>

        <p>-or differential expression-</p>

        <div className="row">
          <div className="col-md">
            <div className="form-group">
              <label htmlFor="comparison1">Comparison 1</label>
              <Select
                isMulti
                name="comparison1"
                id="comparison1"
                value={this.state.comparison1}
                onChange={value => this.setState({ comparison1: value })}
                closeMenuOnSelect={false}
                options={nucleiRegions}
              />
            </div>
          </div>
          <div className="col-md">
            <div className="form-group">
              <label htmlFor="comparison2">Comparison 2</label>
              <Select
                isMulti
                name="comparison2"
                id="comparison2"
                value={this.state.comparison2}
                onChange={value => this.setState({ comparison2: value })}
                closeMenuOnSelect={false}
                options={nucleiRegions}
              />
            </div>
          </div>
        </div>


        <div className="row">
          <div className="col-md">
            <div className="form-group">
              <label htmlFor="sortBy">Sort</label>
              <select className="form-control" id="sortBy" name="sortBy" value={this.state.sortBy} onChange={e => this.setState({ sortBy: e.target.value })}>
                <option value="P">P-Value</option>
                <option value="logFC">log2 fold change</option>
                <option value="t">Moderated t-statistic</option>
                <option value="AveExpr">Average expression</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="fdrMax">FDR Max</label>
              <input className="form-control" type="number" name="fdrMax" id="fdrMax" value={this.state.fdrMax} onChange={e => this.setState({ fdrMax: e.target.value })} />
            </div>
          </div>
          <div className="col-md">
            <div className="form-group">
              <label htmlFor="lfcMin">log2 Fold Change Minimum</label>
              <input className="form-control" type="number" name="lfcMin" id="lfcMin" value={this.state.lfcMin} onChange={e => this.setState({ lfcMin: e.target.value })} />
            </div>
            <div className="form-group">
              <label htmlFor="geneNumber">Top Number of Genes to Plot</label>
              <input className="form-control" type="number" name="geneNumber" id="geneNumber" value={this.state.geneNumber} onChange={e => this.setState({ geneNumber: e.target.value })}  />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="deMode">Mode</label>
              <select className="form-control" id="deMode" name="deMode" value={this.state.deMode} onChange={e => this.setState({ deMode: e.target.value })}>
                <option value="both">Both</option>
                <option value="enrichment">Enrichment</option>
                <option value="depletion">Depletion</option>
              </select>
            </div>
          </div>
        </div>
        <button className="btn btn-primary" onClick={this.handleSubmit}>Submit</button>
      </div>
    );
  }
}

HeatMap.propTypes = {
  match: PropType.object.isRequired,
  history: PropType.object.isRequired,
  cellPopulation: PropType.object.isRequired,
};


export default HeatMap;



// WEBPACK FOOTER //
// ./src/components/Nuclei/HeatMap.jsx